import i18n from 'modules/core/lib/i18n'
import { z } from 'zod'

import {
  EdgeUI,
  NodeUI,
  NodeUIApproval,
  NodeUICondition,
  NodeUIMultiAction,
  NodeUITask,
} from 'modules/workflows/components/Node/types'
import { isNodeUIConditionValid } from 'modules/workflows/components/Node/utils'
import type { FormContext } from 'modules/workflows/components/Sidepanel/schemas/nodeSidepanelSchema'
import type { HandlersConfig } from 'modules/workflows/contexts/WorkflowContext'

// Validation error types
export type WorkflowValidationErrorType =
  | 'INCOMPLETE_BLOCK'
  | 'INVALID_BRANCH_TERMINATION'
  | 'DISCONNECTED_NODE'

export type WorkflowValidationError = {
  type: WorkflowValidationErrorType
  nodeId: string
  message: string
}

export type WorkflowValidationResult = {
  isValid: boolean
  errors: WorkflowValidationError[]
}

// Type guards
const isTaskOrApprovalNode = (node: NodeUI): node is NodeUITask | NodeUIApproval => {
  return node.type === 'task' || node.type === 'approvals'
}

const isConditionNode = (node: NodeUI): node is NodeUICondition => {
  return node.type === 'condition'
}

const isConditionBranchNode = (node: NodeUI): boolean => {
  return node.type === 'conditionIf' || node.type === 'conditionElse'
}

const isTriggerNode = (node: NodeUI): boolean => {
  return node.type === 'trigger'
}

const isValidTerminalNode = (
  node: NodeUI
): node is NodeUITask | NodeUIApproval | NodeUIMultiAction => {
  return node.type === 'task' || node.type === 'approvals' || node.type === 'multiAction'
}

/**
 * Checks if a task or approval node is complete (has handler_name set)
 */
const isActionNodeComplete = (node: NodeUITask | NodeUIApproval): boolean => {
  return !!node.data.handler_name && node.data.handler_name.trim() !== ''
}

/**
 * Gets the validation error message for a specific node
 */
export const getNodeValidationError = (node: NodeUI): string | null => {
  if (isTaskOrApprovalNode(node)) {
    if (!isActionNodeComplete(node)) {
      return i18n.t('workflows.validation.incomplete_action_block')
    }
  }

  if (isConditionNode(node)) {
    if (!isNodeUIConditionValid(node)) {
      return i18n.t('workflows.validation.incomplete_condition_block')
    }
  }

  if (node.type === 'multiAction') {
    const taskCount = (node.data.parallelTasks ?? []).length
    if (taskCount < 2) {
      return i18n.t('workflows.validation.multi_action_min_tasks')
    }
  }

  return null
}

/**
 * Checks if a node is complete based on its type
 */
export const isNodeComplete = (node: NodeUI): boolean => {
  // Trigger nodes are always complete
  if (isTriggerNode(node)) {
    return true
  }

  // Condition branch nodes (conditionIf, conditionElse) are UI-only and always valid
  if (isConditionBranchNode(node)) {
    return true
  }

  // Task and approval nodes need handler_name
  if (isTaskOrApprovalNode(node)) {
    return isActionNodeComplete(node)
  }

  // Condition nodes need valid comparisons
  if (isConditionNode(node)) {
    return isNodeUIConditionValid(node)
  }

  // Multi-action nodes need at least 2 parallel tasks (round-trip with backend fork/join requires it)
  if (node.type === 'multiAction') {
    return (node.data.parallelTasks ?? []).length >= 2
  }

  return true
}

/** Schema for a parallel task as stored in multi-action node data (validates shape before handler schema). */
const parallelTaskForValidationSchema = z
  .object({
    id: z.string(),
    type: z.enum(['task', 'approvals']),
    title: z.string(),
    description: z.string(),
    handler_name: z.string(),
    payload: z.record(z.string(), z.unknown()).optional().default({}),
  })
  .refine((data) => data.handler_name.trim().length > 0, {
    message: 'handler_name must be non-empty',
    path: ['handler_name'],
  })

/**
 * Optional context for workflow validation. Kept for API compatibility; validateWorkflow
 * currently ignores it. Callers may still pass it (e.g. formContext and handlersConfig)
 * without affecting behavior.
 */
export type WorkflowValidationContext = {
  formContext: FormContext
  handlersConfig: HandlersConfig
}

/**
 * Validates each parallel task of a multi-action node has the required shape and a non-empty handler.
 * Does not validate payload against the handler's schema, so it matches single task/approval nodes
 * (which only require handler_name at save time; form validation runs when the user opens the node).
 */
const validateMultiActionChildActions = (nodes: NodeUI[]): WorkflowValidationError[] => {
  const errors: WorkflowValidationError[] = []

  for (const node of nodes) {
    if (node.type !== 'multiAction') continue

    const rawTasks = Array.isArray(node.data.parallelTasks) ? node.data.parallelTasks : []
    if (rawTasks.length < 2) continue

    for (const rawTask of rawTasks) {
      const parsed = parallelTaskForValidationSchema.safeParse(rawTask)
      if (!parsed.success) {
        errors.push({
          type: 'INCOMPLETE_BLOCK',
          nodeId: node.id,
          message: i18n.t('workflows.validation.incomplete_action_block'),
        })
        break
      }
    }
  }

  return errors
}

/**
 * Finds all terminal nodes (nodes with no outgoing edges)
 * Excludes condition branch nodes since they are UI-only constructs
 */
const findTerminalNodes = (nodes: NodeUI[], edges: EdgeUI[]): NodeUI[] => {
  const nodesWithOutgoingEdges = new Set(edges.map((edge) => edge.source))

  return nodes.filter((node) => {
    // Skip condition branch nodes - they're UI-only
    if (isConditionBranchNode(node)) {
      return false
    }

    // Skip condition nodes - they always have outgoing edges to their branches
    if (isConditionNode(node)) {
      return false
    }

    return !nodesWithOutgoingEdges.has(node.id)
  })
}

/**
 * Finds nodes that end each branch of a condition node
 * by following edges from conditionIf and conditionElse nodes
 */
const findBranchTerminalNodes = (
  conditionNode: NodeUICondition,
  nodes: NodeUI[],
  edges: EdgeUI[]
): NodeUI[] => {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]))

  // Find the condition branch nodes (conditionIf, conditionElse) that belong to this condition
  const branchNodes = nodes.filter((n) => {
    if (!isConditionBranchNode(n)) return false
    // Check if there's an edge from the condition node to this branch node
    return edges.some((e) => e.source === conditionNode.id && e.target === n.id)
  })

  const terminalNodes: NodeUI[] = []

  // For each branch, follow the edges to find where it terminates
  for (const branchNode of branchNodes) {
    const outgoingEdges = edges.filter((e) => e.source === branchNode.id)

    if (outgoingEdges.length === 0) {
      // Branch has no outgoing edge - it terminates at the branch node itself
      terminalNodes.push(branchNode)
    } else {
      // Follow the edges to find actual terminal nodes
      for (const edge of outgoingEdges) {
        const targetNode = nodeMap.get(edge.target)
        if (targetNode) {
          // We found a node after the branch - check if this is terminal
          const furtherEdges = edges.filter((e) => e.source === targetNode.id)
          if (furtherEdges.length === 0 && !isConditionNode(targetNode)) {
            terminalNodes.push(targetNode)
          }
        }
      }
    }
  }

  return terminalNodes
}

/**
 * Validates that all branches terminate in Task, Approval, or Multi-action blocks
 */
const validateBranchTermination = (nodes: NodeUI[], edges: EdgeUI[]): WorkflowValidationError[] => {
  const errors: WorkflowValidationError[] = []

  // Find all terminal nodes in the workflow
  const terminalNodes = findTerminalNodes(nodes, edges)
  for (const node of terminalNodes) {
    if (!isValidTerminalNode(node)) {
      if (isTriggerNode(node)) continue

      errors.push({
        type: 'INVALID_BRANCH_TERMINATION',
        nodeId: node.id,
        message: i18n.t('workflows.validation.invalid_branch_termination'),
      })
    }
  }

  // Also check condition node branches specifically
  const conditionNodes = nodes.filter(isConditionNode)

  for (const conditionNode of conditionNodes) {
    const branchTerminals = findBranchTerminalNodes(conditionNode, nodes, edges)

    for (const terminal of branchTerminals) {
      // If a branch terminates at a conditionIf or conditionElse, it means no block was added after
      if (isConditionBranchNode(terminal)) {
        errors.push({
          type: 'INVALID_BRANCH_TERMINATION',
          nodeId: terminal.id,
          message: i18n.t('workflows.validation.empty_branch'),
        })
      }
    }
  }

  return errors
}

/**
 * Validates that all blocks are complete
 */
const validateBlockCompleteness = (nodes: NodeUI[]): WorkflowValidationError[] => {
  const errors: WorkflowValidationError[] = []

  for (const node of nodes) {
    // Skip condition branch nodes
    if (isConditionBranchNode(node)) continue

    if (!isNodeComplete(node)) {
      const errorMessage = getNodeValidationError(node)
      errors.push({
        type: 'INCOMPLETE_BLOCK',
        nodeId: node.id,
        message: errorMessage || i18n.t('workflows.validation.incomplete_block'),
      })
    }
  }

  return errors
}

/**
 * Main validation function for the entire workflow.
 * Multi-action children are validated for shape and non-empty handler only (context is kept for API compatibility).
 */
export const validateWorkflow = (
  nodes: NodeUI[],
  edges: EdgeUI[],
  _context?: WorkflowValidationContext
): WorkflowValidationResult => {
  const errors: WorkflowValidationError[] = []

  // Validate block completeness
  errors.push(...validateBlockCompleteness(nodes))

  // Validate branch termination
  errors.push(...validateBranchTermination(nodes, edges))

  // Validate each multi-action child has required shape and handler (same as single task/approval: no payload schema at save)
  errors.push(...validateMultiActionChildActions(nodes))

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Gets a summary of validation errors for display
 */
export const getValidationErrorSummary = (errors: WorkflowValidationError[]): string => {
  if (errors.length === 0) {
    return ''
  }

  const incompleteBlocks = errors.filter((e) => e.type === 'INCOMPLETE_BLOCK')
  const invalidTerminations = errors.filter((e) => e.type === 'INVALID_BRANCH_TERMINATION')

  const parts: string[] = []

  if (incompleteBlocks.length > 0) {
    parts.push(
      i18n.t('workflows.validation.summary.incomplete_blocks', { count: incompleteBlocks.length })
    )
  }

  if (invalidTerminations.length > 0) {
    parts.push(
      i18n.t('workflows.validation.summary.invalid_terminations', {
        count: invalidTerminations.length,
      })
    )
  }

  return parts.join('. ')
}
